import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { HelpCenterCategory } from '../models/helpCenterCategory';
import { HelpCenterArticle } from '../models/helpCenterArticle';
import { getHelpCenterArticle, getHelpCenterCategories } from '../services/helpCenterService';
import { groupHelpCenterCategories } from '../functions/groupHelpCenterCategories';
import { getHelpCenterTrail } from '../functions/getHelpCenterTrail';
import { HelpCenterLayout } from '../components/helpCenterLayout';
import { HelpArticleBody } from '../components/helpArticleBody';

// Avståndet ovanför ett avsnitt man hoppar till, så att rubriken hamnar under den fasta headern. Samma som sidans
// eget avstånd till innehållet (PageContainer).
const SECTION_SCROLL_MARGIN = '80px';

const getHashId = () => {
  try {
    return decodeURIComponent(window.location.hash.slice(1));
  } catch {
    return '';
  }
};

export const HelpArticlePage = () => {
  const { t } = useTranslation();
  const { slug = '' } = useParams<{ slug: string }>();

  const [categories, setCategories] = useState<HelpCenterCategory[]>([]);
  const [article, setArticle] = useState<HelpCenterArticle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<'notFound' | 'failed' | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Som kategorisidan: 404 utan toast visas som en text med väg tillbaka, andra fel med en egen text, och ett sent
  // svar för en artikel man redan lämnat skriver inte över den nya.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setArticle(null);
      try {
        const [categoryResult, articleResult] = await Promise.all([getHelpCenterCategories(), getHelpCenterArticle(slug)]);
        if (!cancelled) {
          setCategories(categoryResult.data);
          setArticle(articleResult.data);
        }
      } catch (e) {
        ConsoleHelper.log(e);
        if (!cancelled) setError((e as { status?: number })?.status === 404 ? 'notFound' : 'failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // En adress med #avsnitt (delad länk eller omladdning): webbläsaren försöker skrolla innan artikeln finns, så det
  // görs här när den visas. Bilderna ovanför avsnittet har ingen höjd förrän de laddats och flyttar det nedåt, så det
  // skrollas igen när de är klara — tills läsaren själv börjar skrolla.
  useEffect(() => {
    const body = bodyRef.current;
    const id = getHashId();
    if (loading || !article || !body || !id) return undefined;

    const target = body.querySelector(`[id="${CSS.escape(id)}"], a[name="${CSS.escape(id)}"]`);
    if (!target) return undefined;
    const scrollToSection = () => target.scrollIntoView();
    scrollToSection();

    const images = Array.from(body.querySelectorAll('img')).filter((img) => !img.complete);
    const userEvents = ['wheel', 'touchstart', 'keydown'];
    const stop = () => {
      images.forEach((img) => {
        img.removeEventListener('load', scrollToSection);
        img.removeEventListener('error', scrollToSection);
      });
      userEvents.forEach((name) => window.removeEventListener(name, stop));
    };
    images.forEach((img) => {
      img.addEventListener('load', scrollToSection);
      img.addEventListener('error', scrollToSection);
    });
    userEvents.forEach((name) => window.addEventListener(name, stop));
    return stop;
  }, [article, loading]);

  const category = categories.find((x) => x.id === article?.categoryId);
  const section = useMemo(
    () => groupHelpCenterCategories(categories).find((x) => x.cards.some((card) => card.id === article?.categoryId)),
    [categories, article],
  );

  const trail = !error && article ? [...getHelpCenterTrail(section, category), { key: `article-${article.slug}`, label: article.title }] : [];

  return (
    <HelpCenterLayout trail={trail} section={section} currentCategoryId={article?.categoryId}>
      <Box sx={{ minHeight: loading ? '10rem' : undefined }}>
        <LoadingOverlay loading={loading} />
        {!loading && (error || !article) && (
          <Box sx={{ mt: 2 }}>
            <Typography color='text.secondary'>{t(error === 'failed' ? 'help:loadFailed' : 'help:articleNotFound')}</Typography>
            <MuiLink component={Link} to={RoutesEnum.Help} sx={{ display: 'inline-block', mt: 1 }}>
              {t('help:backToHelp')}
            </MuiLink>
          </Box>
        )}
        {!loading && !error && article && (
          <>
            <MainContentHeader title={article.title} />
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              {t('help:updatedAt', { date: DateHelper.getDate(article.updatedAt) })}
            </Typography>
            {/* Ankarlänkar (t.ex. artikelns innehållsförteckning) stannar en bit ovanför avsnittet, så att rubriken inte
                hamnar bakom den fasta headern. Bara här: panelen och förhandsvisningen har ingen header ovanpå. */}
            <Box ref={bodyRef} sx={{ '& [id], & a[name]': { scrollMarginTop: SECTION_SCROLL_MARGIN } }}>
              <HelpArticleBody bodyHtml={article.bodyHtml} />
            </Box>
            {/* Tillbaka upp efter en lång artikel. Visas alltid; under en kort artikel stör den inte. Det är sidans
                main (PageContainer) som skrollar, inte fönstret. */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                size='small'
                startIcon={<ArrowUpward fontSize='small' />}
                onClick={(e) => e.currentTarget.closest('main')?.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                {t('help:backToTop')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </HelpCenterLayout>
  );
};
